import { useMutation, useQueryClient } from '@tanstack/react-query';
import { patchAutoUpgrade } from '../../api/subscriptions';

interface AutoUpgradeToggleProps {
  subscriptionId: string;
  autoUpgrade: number | undefined;
  appName: string;
}

export default function AutoUpgradeToggle({
  subscriptionId,
  autoUpgrade,
  appName,
}: AutoUpgradeToggleProps) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (enabled: boolean) => patchAutoUpgrade(subscriptionId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  // Only render for UniFi subscriptions
  if (appName !== 'UniFi') {
    return null;
  }

  // Convert D1 integer (0/1) to boolean - default to enabled if undefined
  const isEnabled = autoUpgrade !== 0;

  const handleToggle = () => {
    mutation.mutate(!isEnabled);
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg
            className="h-5 w-5 text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span className="font-medium text-gray-900">Automatic Upgrades</span>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={isEnabled}
          aria-label="Toggle automatic upgrades"
          onClick={handleToggle}
          disabled={mutation.isPending}
          className={`
            relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
            transition-colors duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-offset-2
            ${isEnabled ? 'bg-green-500 focus:ring-green-500' : 'bg-amber-500 focus:ring-amber-500'}
            ${mutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <span
            className={`
              pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
              transition duration-200 ease-in-out
              ${isEnabled ? 'translate-x-5' : 'translate-x-0'}
            `}
          />
        </button>
      </div>

      <div className="text-sm text-gray-600 space-y-2">
        <p>
          When enabled, your UniFi network application controller software will be automatically
          upgraded to new stable releases. We test each stable release version which generally
          takes 2-4 weeks before rolling out the upgrade.
        </p>
        <p>
          You can always upgrade manually using the Software Upgrade action below, regardless of
          this setting.
        </p>
      </div>

      {mutation.isError && (
        <p className="mt-3 text-sm text-red-600">
          {(mutation.error as Error)?.message || 'Failed to update auto-upgrade setting'}
        </p>
      )}
    </div>
  );
}
